import { useLayoutEffect, useMemo, useRef } from 'react';

import type { IFRAME } from '@modules/types';

import debounce from 'lodash/debounce';

import { USE_IFRAME_HEIGHT_DEBOUNCE_MS } from '@constants/hooks';
import { sendPostMessage } from '@utils/sendPostMessage';

export const useIframeHeight = (iframe: IFRAME, gap = 4) => {
  const isIframe = window.self !== window.top;
  const previousHeight = useRef(0);

  const debouncedSendHeight = useMemo(
    () =>
      debounce((height: number) => {
        if (height !== previousHeight.current) {
          previousHeight.current = height;
          sendPostMessage({
            keyName: 'height',
            data: height + gap,
            type: `${iframe}_IFRAME_HEIGHT`,
          });
        }
      }, USE_IFRAME_HEIGHT_DEBOUNCE_MS),
    [gap, iframe]
  );

  useLayoutEffect(() => {
    if (!isIframe) {
      return;
    }

    const initialHeight = document.body.scrollHeight;
    debouncedSendHeight(initialHeight);

    const observer = new ResizeObserver(() => {
      const currentHeight = document.body.scrollHeight;
      debouncedSendHeight(currentHeight);
    });

    observer.observe(document.body);

    return () => {
      observer.disconnect();
      debouncedSendHeight.cancel();
    };
  }, [iframe, isIframe, debouncedSendHeight]);
};
